<template>
    <div :class="{ 'topnav-container': navbarFixed }">
        <nav
            class="navbar navbar-expand-lg navbar-light top-nav border-bottom border-gray bg-white"
            :class="{ 'fixed-top': navbarFixed }"
            v-show="!hideNav"
        >
            <!-- Brand Icon -->
            <router-link
                v-if="customLogoNotConfigured || !allowCustomLogo"
                :to="isAuthorOrAdmin ? '/library/' : '/'"
                class="navbar-brand py-0"
                aria-label="Link to the home view"
            >
                <img src="/static/general/img/affinity-blue.svg" height="18" alt="Affinity Logo" />
            </router-link>

            <!-- Organization Custom Logo -->
            <div v-else class="logo-sm d-flex align-items-center rounded-circle custom-logo-circle">
                <img :src="customLogoUrl" />
            </div>

            <!-- Collapsed-Navbar Toggler -->
            <button
                class="navbar-toggler border-0"
                type="button"
                data-toggle="collapse"
                data-target="#navbarSupportedContent"
                aria-controls="navbarSupportedContent"
                aria-expanded="false"
                aria-label="Toggle navigation"
            >
                <span class="navbar-toggler-icon"></span>
            </button>

            <!-- Navbar Items -->
            <div class="collapse navbar-collapse my-2 my-lg-0 mw-100" id="navbarSupportedContent">
                <ul class="navbar-nav mr-auto d-flex align-items-lg-center" v-if="!pageViewActive">
                    <!-- Student Items -->
                    <template v-if="isStudent && !communityViewActive">
                        <li class="nav-item" @click="collapseTopNav()">
                            <router-link to="/history/" active-class="active" class="nav-link">History</router-link>
                        </li>
                    </template>

                    <!-- Community Items -->
                    <template v-if="(communityViewActive && !userIsAnonymous) || communityUserOnly">
                        <li class="nav-item" @click="collapseTopNav()">
                            <router-link to="/community/browse" active-class="active" class="nav-link"
                                >Browse</router-link
                            >
                        </li>
                        <li class="nav-item" @click="collapseTopNav()">
                            <router-link to="/community/sessions" active-class="active" class="nav-link"
                                >History</router-link
                            >
                        </li>
                        <li v-if="showCommunityCreateBtn" @click="collapseTopNav()" class="nav-item">
                            <a @click="openCommunityCreateModal()" class="nav-link"> Create </a>
                        </li>
                    </template>

                    <template v-if="userIsAnonymous">
                        <li class="nav-item" @click="collapseTopNav()">
                            <router-link to="/community/browse" active-class="active" class="nav-link"
                                >Community</router-link
                            >
                        </li>
                        <li class="nav-item" @click="collapseTopNav()">
                            <a href="https://affinitylearning.ca/" rel="noopener" target="_blank" class="nav-link">
                                <span>About</span>
                                <span class="icon-glyphicons-basic-square-new-window new-window-icon ml-1"></span>
                            </a>
                        </li>
                    </template>

                    <!-- Author & Admin Items -->
                    <template v-if="isAuthorOrAdmin && !communityViewActive">
                        <li class="nav-item" @click="collapseTopNav()">
                            <router-link to="/library/" active-class="active" class="nav-link" data-tour="library"
                                >Library</router-link
                            >
                        </li>
                        <li class="nav-item" @click="collapseTopNav()">
                            <router-link to="/pages/" active-class="active" class="nav-link" data-tour="pages">
                                Pages
                            </router-link>
                        </li>
                        <li class="nav-item" @click="collapseTopNav()" v-if="isAdmin">
                            <router-link to="/users/" active-class="active" class="nav-link">
                                Users
                            </router-link>
                        </li>
                        <li class="nav-item" @click="collapseTopNav()">
                            <router-link to="/reports/" active-class="active" class="nav-link"> Reports </router-link>
                        </li>
                        <li class="nav-item ml-lg-4 my-lg-0 my-3">
                            <library-item-search
                                @selected="handleLibraryItemSelection($event)"
                                :clear-after-selection="true"
                                :constrain-width="true"
                                :item-types="['owned_case', 'community_reference', 'folder']"
                            ></library-item-search>
                        </li>
                    </template>
                </ul>
                <ul class="navbar-nav ml-auto">
                    <li
                        v-show="userIsAnonymous && !loading && !hideLoginPrompts"
                        class="nav-item"
                        @click="collapseTopNav()"
                    >
                        <a href="/login/" class="nav-link">Login</a>
                    </li>
                    <li
                        v-show="userIsAnonymous && !loading && !hideLoginPrompts"
                        class="nav-item"
                        @click="collapseTopNav()"
                    >
                        <a href="/get-started" class="nav-link">Create an Account</a>
                    </li>
                    <li class="nav-item" v-if="currentPricingTier === 'basic'">
                        <span class="nav-link">
                            <info-popover :content="currentPricingTierReadout" placement="bottom"
                                ><a @click="$refs.billingView.open()" class="badge badge-highlight align-middle">
                                    Upgrade
                                </a></info-popover
                            >
                        </span>
                    </li>
                    <li class="nav-item dropdown">
                        <a
                            class="nav-link"
                            href="#"
                            data-toggle="modal"
                            data-target="#support-modal"
                            aria-label="Open help dialog"
                            data-tour="support"
                        >
                            <span class="toolbar-icon icon-glyphicons-basic-circle-question align-middle"></span>
                            <span class="pl-2 d-lg-none">Support</span>
                        </a>
                    </li>
                    <li class="nav-item dropdown mr-1">
                        <a
                            class="nav-link"
                            href="#"
                            data-toggle="modal"
                            data-target="#feedback-modal"
                            aria-label="Open feedback submission dialog"
                        >
                            <span class="toolbar-icon icon-glyphicons-basic-announcement align-middle"></span>
                            <span class="pl-2 d-lg-none">Feedback</span>
                        </a>
                    </li>
                    <!-- Organization Dropdown -->
                    <li v-show="user.id && !pageViewActive" class="nav-item dropdown my-2 my-lg-0 mr-lg-1 ml-lg-2">
                        <!-- Dropdown Label -->
                        <a
                            class="nav-link nav-link-org-dropdown"
                            href="#"
                            role="button"
                            data-tour="workspace"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                            id="organizationDropdown"
                        >
                            <div class="d-flex">
                                <div class="d-flex align-items-center">
                                    <div
                                        v-if="showOrganizationCustomLogo"
                                        class="d-flex align-items-center rounded-circle flex-shrink-0 flex-grow-0 custom-logo-circle dropdown-org-logo"
                                    >
                                        <img :src="currentOrganizationCustomLogoUrl" />
                                    </div>
                                    <div class="org-dropdown-label flex-shrink-0 flex-grow-0">
                                        {{ orgDropdownLabel }}
                                    </div>
                                </div>
                                <div>
                                    <span
                                        aria-hidden="true"
                                        class="icon-glyphicons-basic-chevron-down chevron ml-1"
                                    ></span>
                                </div>
                            </div>
                        </a>
                        <!-- Dropdown Menu -->
                        <div
                            class="dropdown-menu dropdown-menu-right org-dropdown-list shadow-sm border-gray medium"
                            aria-labelledby="organizationDropdown"
                        >
                            <!-- Org List -->
                            <template v-if="memberships.length">
                                <h6 class="dropdown-header px-3">Switch to...</h6>
                                <a
                                    class="dropdown-item px-3"
                                    v-for="membership in memberships"
                                    :key="membership.id"
                                    @click="setActiveMembership(membership)"
                                    :class="{
                                        active:
                                            membership.organization.id == currentOrganization.id &&
                                            !communityViewActive,
                                    }"
                                >
                                    <div class="d-flex align-items-center">
                                        <div class="flex-grow-1 mr-3 pr-1">
                                            {{ membership.organization.name }}
                                        </div>
                                        <div class="flex-grow-0">
                                            <span class="badge badge-light badge-small align-middle">
                                                {{ displayRoleName(membership.role) }}
                                            </span>
                                        </div>
                                    </div>
                                </a>
                            </template>

                            <!-- Create Workspace -->
                            <div class="dropdown-divider" v-if="membershipCount > 0"></div>
                            <a @click="openCreateOrganization()" class="dropdown-item px-3">
                                <span class="icon icon-glyphicons-basic-plus icon-mr"></span>
                                <span>Create a new workspace</span>
                            </a>

                            <!-- Community -->
                            <div class="dropdown-divider"></div>
                            <router-link
                                to="/community/browse"
                                class="dropdown-item px-3"
                                :class="{ active: communityViewActive }"
                            >
                                <span
                                    class="icon icon-glyphicons-basic-world-east align-middle icon-mr"
                                    style="padding-top: 1px"
                                ></span>
                                <span class="align-middle">Community</span>
                            </router-link>
                        </div>
                    </li>
                    <!-- Manage -->
                    <li class="nav-item dropdown" v-if="communityViewActive && !userIsAnonymous">
                        <a
                            class="nav-link"
                            :class="{ active: isViewingCommunityPublishers }"
                            id="community-manage-dropdown"
                            role="button"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <span>Manage</span>
                            <span class="icon-glyphicons-basic-chevron-down chevron ml-1"></span>
                        </a>
                        <div
                            class="dropdown-menu dropdown-menu-right shadow-sm border-gray medium"
                            aria-labelledby="community-manage-dropdown"
                        >
                            <router-link to="/community/publishers" active-class="active" class="dropdown-item">
                                Publisher Profiles
                            </router-link>
                        </div>
                    </li>
                    <li v-show="isAdmin && !communityViewActive && !pageViewActive" class="nav-item dropdown">
                        <a
                            class="nav-link"
                            :class="{ active: isViewingOrgManage }"
                            id="org-manage-dropdown"
                            role="button"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <span>Manage</span>
                            <span class="icon-glyphicons-basic-chevron-down chevron ml-1"></span>
                        </a>
                        <div
                            class="dropdown-menu dropdown-menu-right shadow-sm border-gray medium"
                            aria-labelledby="org-manage-dropdown"
                            @click="collapseTopNav()"
                        >
                            <!-- Org Settings -->
                            <template>
                                <h6 class="dropdown-header">Account & Settings</h6>
                                <router-link
                                    :to="{ name: 'OrganizationDetails' }"
                                    class="dropdown-item"
                                    active-class="active"
                                >
                                    <span class="icon icon-100 icon-glyphicons-basic-cogwheel mr-2"></span>
                                    <span class="align-middle">Workspace Settings</span>
                                </router-link>
                                <router-link
                                    :to="{ name: 'LMSIntegrationSettings' }"
                                    class="dropdown-item"
                                    active-class="active"
                                >
                                    <span class="icon icon-100 icon-glyphicons-basic-power-cord-plug mr-2"></span>
                                    <span class="align-middle">LMS Integrations</span>
                                </router-link>
                                <a
                                    v-if="organizationBillingEnabled"
                                    class="dropdown-item"
                                    @click="$refs.billingView.open()"
                                >
                                    <span class="icon icon-100 icon-glyphicons-basic-credit-card mr-2"></span>
                                    <span class="align-middle">Plan & Billing</span>
                                </a>
                                <div class="dropdown-divider"></div>
                                <h6 class="dropdown-header">Community</h6>
                                <router-link to="/community/publishers" active-class="active" class="dropdown-item">
                                    <span class="icon icon-100 icon-glyphicons-basic-education icon-mr"></span>
                                    <span class="align-middle">Publisher Profiles</span>
                                </router-link>
                            </template>
                        </div>
                    </li>
                    <li v-show="!userIsAnonymous" class="nav-item dropdown">
                        <a
                            class="nav-link"
                            :class="{ active: profileView }"
                            href="#"
                            id="settingsDropdown"
                            role="button"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <span>Profile</span>
                            <span class="icon-glyphicons-basic-chevron-down chevron ml-1"></span>
                        </a>
                        <div
                            class="dropdown-menu dropdown-menu-right shadow-sm border-gray medium"
                            aria-labelledby="settingsDropdown"
                            @click="collapseTopNav()"
                        >
                            <router-link to="/history/" active-class="active" class="dropdown-item">
                                Activity
                            </router-link>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" rel="noopener" target="_blank" href="/privacy">
                                <span class="mr-1">Privacy</span>
                                <span class="icon icon-100 icon-glyphicons-basic-square-new-window icon-mr"></span>
                            </a>
                            <a class="dropdown-item" rel="noopener" target="_blank" href="/accessibility">
                                <span class="mr-1">Accessibility</span>
                                <span class="icon icon-100 icon-glyphicons-basic-square-new-window icon-mr"></span>
                            </a>
                            <a class="dropdown-item" rel="noopener" target="_blank" href="/legal">
                                <span class="mr-1">Legal</span>
                                <span class="icon icon-100 icon-glyphicons-basic-square-new-window icon-mr"></span>
                            </a>
                            <div v-if="isStaff">
                                <div class="dropdown-divider"></div>
                                <a class="dropdown-item" href="/admin/" target="_blank" id="change-password">
                                    <span class="mr-1">Staff Portal</span>
                                    <span class="icon icon-100 icon-glyphicons-basic-square-new-window icon-mr"></span>
                                </a>
                            </div>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" href="/change-password/" id="change-password">Change Password</a>
                            <a class="dropdown-item" @click="logout()">Log Out</a>
                        </div>
                    </li>
                </ul>
            </div>
        </nav>

        <!-- Feedback Modal -->
        <base-modal id="feedback-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Have Feedback? </template>
            <template>
                <p class="mb-2">Leave a suggestion for our team.</p>
                <textarea
                    v-model="feedback"
                    class="form-control"
                    rows="10"
                    placeholder="Enter a suggestion or some feedback"
                    aria-label="Your feedback"
                ></textarea>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="sendFeedback()" class="btn btn-primary" type="button">Send</button>
            </template>
        </base-modal>

        <!-- Support Modal -->
        <base-modal id="support-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-100 icon-glyphicons-basic-circle-question align-middle icon-mr"></span>
                <span class="align-middle">Help & Support</span>
            </template>
            <template>
                <ul class="nav mb-3 mt-2 font-weight-500">
                    <li
                        class="nav-item tab-nav tab-nav-highlight mr-2"
                        role="button"
                        @click="selectedSupportTab = 'resources'"
                    >
                        <div class="tab-nav-link p-2 medium" :class="{ active: selectedSupportTab === 'resources' }">
                            <span class="icon icon-glyphicons-basic-square-play align-middle icon-mr"></span>
                            <span class="align-middle mt-1">Walkthroughs</span>
                        </div>
                    </li>
                    <li
                        class="nav-item tab-nav tab-nav-highlight mr-1"
                        role="button"
                        @click="selectedSupportTab = 'request'"
                    >
                        <div class="tab-nav-link p-2 medium" :class="{ active: selectedSupportTab === 'request' }">
                            <span class="icon icon-glyphicons-basic-user align-middle icon-mr"></span>
                            <span class="align-middle mt-1">Request Support </span>
                        </div>
                    </li>
                </ul>
                <div class="pt-3" v-show="selectedSupportTab === 'resources'">
                    <div v-for="(video, index) in walkthroughVideos" :key="video.href">
                        <div class="py-1">
                            <a :href="video.href" rel="noopener" target="blank">
                                <div class="d-flex align-items-center">
                                    <div class="pr-4">
                                        <img
                                            :src="video.thumbnailPath"
                                            height="95"
                                            width="160"
                                            class="rounded-lg shadow-sm obj-cover"
                                            alt=""
                                        />
                                    </div>
                                    <div>
                                        <div class="pb-1">
                                            <span
                                                class="icon icon-glyphicons-basic-video-play align-middle icon-mr"
                                            ></span>
                                            <span class="align-middle mr-1">{{ video.name }}</span>
                                            <span
                                                class="icon icon-100 icon-glyphicons-basic-square-new-window align-middle"
                                            ></span>
                                        </div>
                                        <div class="text-secondary small">{{ video.description }}</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                        <hr v-if="index !== walkthroughVideos.length - 1" />
                    </div>
                </div>
                <div class="pt-2" v-show="selectedSupportTab === 'request'">
                    <p>Send us a message and someone on our team will get back to you shortly.</p>
                    <base-form v-model="support" :fields="supportFormFields" ref="supportForm"></base-form>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    v-show="selectedSupportTab === 'request'"
                    @click="sendSupport()"
                    class="btn btn-primary"
                    type="button"
                    :disabled="modalLoading"
                >
                    Send
                </button>
            </template>
        </base-modal>

        <!-- Community Terms Modal -->
        <base-modal id="community-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-world-east mr-2"></span>
                <span>Affinity Community</span>
            </template>
            <template>
                <div>
                    <p class="mb-3" id="community-statement">
                        Affinity Community is a free and open version of the Affinity Learning Platform, designed for
                        open and collaborative knowledge sharing. All content created on the community platform is
                        licensed under All content published on Affinity Community is licensed under
                        <a :href="creativeCommonsLicenseUrl" rel="noopener" target="_blank">Creative Commons</a>.
                        Authors and their associated institutions are always clearly credited for their contributions.
                        Accept the terms and privacy policy to start creating your own learning experiences on the community
                        platform.
                    </p>
                    <div class="form-group form-check">
                        <input id="checkterms" type="checkbox" class="form-check-input" v-model="termsCheckbox" />
                        <label class="form-check-label" for="checkterms">
                            I agree to the
                            <a href="/terms" target="_blank">Terms & Conditions</a> and
                            <a href="/privacy" target="_blank">Privacy Policy</a>.
                        </label>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="acceptTerms()"
                    :disabled="modalLoading || !termsCheckbox"
                    class="btn btn-primary"
                    type="button"
                >
                    Accept
                </button>
            </template>
        </base-modal>

        <!-- Legacy Migration Modal -->
        <base-modal id="legacy-create-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Community Content Has Moved </template>
            <template>
                <div class="py-3">
                    <div class="d-flex justify-content-center mb-4 pb-2">
                        <img
                            src="/static/general/img/screenshots/workspacemenu.png"
                            class="w-100 rounded shadow-sm workspace-screenshot"
                            alt=""
                        />
                    </div>
                    <p class="text-secondary text-center mb-4 px-2">
                        Your community content has been moved to a personal workspace for you to continue editing.
                    </p>
                    <div class="d-flex justify-content-center">
                        <button
                            @click="navigateToLegacyCommunityOrg()"
                            class="btn btn-primary"
                            type="button"
                            :disabled="modalLoading"
                        >
                            Go To My Workspace
                        </button>
                    </div>
                </div>
            </template>
        </base-modal>

        <!-- Create First Organization Modal -->
        <base-modal id="create-first-organization-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Create a Workspace </template>
            <template>
                <div class="pb-3 pt-2">
                    <div class="d-flex justify-content-center mb-4 pb-2">
                        <img src="/static/general/img/undraw_box.svg" height="160" alt="" />
                    </div>
                    <p class="text-center mb-4 px-2">Create a new workspace for you and your team.</p>
                    <base-form
                        v-model="newOrganization"
                        :fields="createOrganizationFields"
                        ref="createOrganizationForm"
                        class="pb-3 px-sm-3"
                    ></base-form>
                    <div class="d-flex justify-content-center">
                        <button
                            @click="createOrganization()"
                            class="btn btn-primary"
                            type="button"
                            :disabled="modalLoading || !newOrganizationIsValid"
                        >
                            Create My Workspace
                        </button>
                    </div>
                </div>
            </template>
        </base-modal>

        <!-- Create Organization Modal -->
        <base-modal id="create-organization-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Create New Workspace </template>
            <template>
                <div class="pb-3 pt-2">
                    <div class="d-flex justify-content-center mb-4 pb-2">
                        <img src="/static/general/img/undraw_box.svg" height="160" alt="" />
                    </div>
                    <div v-if="hasFreeTierMembership && !isStaff">
                        <p class="font-weight-500 text-center mb-3">You're currently using your free workspace.</p>
                        <p class="text-center text-secondary">
                            To create a new workspace, upgrade or deactivate
                            <a href="" @click="navigateToFreeTierMembership()" role="button">your current one</a>.
                        </p>
                    </div>
                    <div v-else>
                        <p class="text-center mb-4 px-2">Create a new workspace to build learning experiences in.</p>
                        <base-form
                            v-model="newOrganization"
                            :fields="createOrganizationFields"
                            ref="createOrganizationForm"
                            class="pb-3 px-sm-3"
                        ></base-form>
                        <div class="d-flex justify-content-center">
                            <button
                                @click="createOrganization()"
                                class="btn btn-primary"
                                type="button"
                                :disabled="modalLoading || !newOrganizationIsValid"
                            >
                                Create Workspace
                            </button>
                        </div>
                    </div>
                </div>
            </template>
        </base-modal>

        <!-- Welcome Modal -->
        <base-modal id="welcome-modal" :centered="true">
            <template>
                <div class="d-flex justify-content-center mb-4 pb-2 pt-4">
                    <img src="/static/general/img/undraw_box.svg" height="150" alt="" />
                </div>
                <div class="mb-3">
                    <div>
                        <p class="text-center mb-3 h5 font-weight-bold">Welcome to your Affinity workspace</p>
                        <p class="text-center mb-3 text-secondary">Get oriented by taking a quick tour.</p>
                    </div>
                    <div class="d-flex justify-content-center pt-3 pb-4">
                        <button class="btn btn-link mr-1" type="button" data-dismiss="modal">Skip Tour</button>
                        <button
                            class="btn btn-primary"
                            type="button"
                            data-dismiss="modal"
                            @click="startTour('author_nav')"
                        >
                            Take a Tour
                        </button>
                    </div>
                    <support-request-prompt class="text-center" />
                </div>
            </template>
        </base-modal>

        <!-- Privacy Update Modal -->
        <base-modal ref="privacyNoticeUpdate" :centered="true">
            <template>
                <div class="d-flex justify-content-end">
                    <div class="flex-grow-0">
                        <button
                            type="button"
                            class="close d-flex align-items-center"
                            data-dismiss="modal"
                            aria-label="Close"
                            style=""
                        >
                            <span
                                class="icon icon-glyphicons-basic-menu-close align-middle"
                                style="font-size: 24px"
                            ></span>
                        </button>
                    </div>
                </div>
                <div class="d-flex justify-content-center mb-4 pb-2 pt-1">
                    <img src="/static/general/img/undraw_no_data.svg" height="120" alt="" />
                </div>
                <div class="mb-3">
                    <div>
                        <p class="text-center mb-3 h6 font-weight-bold">We've updated our Privacy Notice.</p>
                        <p class="text-center mb-4">
                            You can read the full updated notice 
                            <a href="/privacy" target="_blank">
                            here
                            <span class="icon-glyphicons-basic-square-new-window new-window-icon"></span>
                            </a>.
                        </p>
                    </div>
                </div>
                <div class="d-flex justify-content-center pb-2">
                    <button class="btn btn-light" data-dismiss="modal">Close</button>
                </div>
            </template>
        </base-modal>

        <!-- Guided Tour -->
        <v-tour name="orgDropdownTour" :steps="selectedTourSteps" :options="{ startTimeout: 300, highlight: true }">
            <template slot-scope="tour">
                <transition name="fade">
                    <v-step
                        v-if="tour.steps[tour.currentStep]"
                        :key="tour.currentStep"
                        :step="tour.steps[tour.currentStep]"
                        :previous-step="tour.previousStep"
                        :next-step="tour.nextStep"
                        :stop="tour.stop"
                        :skip="tour.skip"
                        :is-first="tour.isFirst"
                        :is-last="tour.isLast"
                        :labels="tour.labels"
                        :highlight="tour.highlight"
                    >
                        <template>
                            <div slot="header" class="d-flex align-content-start mb-2">
                                <div class="flex-grow-1 mr-2">
                                    <div class="h5 mb-0">
                                        <span
                                            v-if="tour.steps[tour.currentStep].iconClass"
                                            class="icon align-middle icon-mr"
                                            :class="[tour.steps[tour.currentStep].iconClass]"
                                        ></span>
                                        <span class="align-middle">{{ tour.steps[tour.currentStep].header }}</span>
                                        <span
                                            v-if="tour.steps[tour.currentStep].showNewBadge"
                                            class="align-middle badge badge-primary ml-2"
                                            style="font-size: 55%"
                                            >NEW</span
                                        >
                                    </div>
                                </div>
                                <div class="flex-grow-0">
                                    <button
                                        @click="tour.stop"
                                        type="button"
                                        class="btn btn-icon text-light p-0"
                                        aria-label="Close"
                                        style="margin-top: -6px"
                                    >
                                        <span class="icon icon-glyphicons-basic-menu-close"></span>
                                    </button>
                                </div>
                            </div>
                            <div slot="content" class="medium mb-4">
                                {{ tour.steps[tour.currentStep].content }}
                            </div>
                            <div slot="actions">
                                <div class="d-flex">
                                    <div class="flex-grow-1">
                                        <button
                                            @click="tour.stop"
                                            class="btn btn-sm btn-link text-light px-1"
                                            style="margin-left: -5px"
                                        >
                                            Skip Tour
                                        </button>
                                    </div>
                                    <div class="flex-grow-0">
                                        <button @click="tour.nextStep" class="btn btn-sm btn-light" v-if="!tour.isLast">
                                            Next
                                        </button>
                                        <button v-else @click="tour.stop" class="btn btn-sm btn-light">
                                            <span v-if="tour.steps.length > 1">End Tour</span>
                                            <span v-else>Got It</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </v-step>
                </transition>
            </template>
        </v-tour>

        <billing-view ref="billingView"></billing-view>
    </div>
</template>

<script>
import http from "@/axios.js"
import { roleNames } from "@/assets/js/settings.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import BillingView from "@/components/BillingView"
import SupportRequestPrompt from "@/components/SupportRequestPrompt"
import LibraryItemSearch from "@/components/LibraryItemSearch"
import InfoPopover from "@/components/InfoPopover"

export default {
    name: "TopNav",
    components: {
        BaseModal,
        BaseForm,
        BillingView,
        SupportRequestPrompt,
        LibraryItemSearch,
        InfoPopover,
    },
    data() {
        return {
            feedback: "",
            support: {
                phone: null,
                email: null,
                message: "",
            },
            selectedSupportTab: "resources",
            modalLoading: false,
            termsCheckbox: false,
            createOrganizationFields: [
                {
                    key: "name",
                    placeholder: "Workspace Name",
                    type: "text",
                    maxlength: 100,
                    subtext: "Provide a short identifier for your new workspace.",
                    required: true,
                },
            ],
            newOrganization: {
                name: "",
            },
            selectedTourId: "author_nav",
            tourSteps: {
                author_nav: [
                    {
                        target: "[data-tour='library']",
                        header: "Library",
                        content: "Collaborate with your team to create learning experiences here.",
                        iconClass: "icon-glyphicons-basic-book-library",
                    },
                    {
                        target: "[data-tour='pages']",
                        header: "Landing Pages",
                        content: "Create customized landing pages and send them to your learners as links.",
                        iconClass: "icon-glyphicons-basic-files-queue",
                    },
                    {
                        target: "[data-tour='support']",
                        header: "Help & Support",
                        content: "Find video walkthroughs or request support from a staff member.",
                        iconClass: "icon-glyphicons-basic-circle-question",
                    },
                    {
                        target: "[data-tour='workspace']",
                        header: "Switch between workspaces",
                        content: "Switch between workspaces or the community library using this dropdown.",
                        iconClass: "icon-glyphicons-basic-switch",
                    },
                ],
            },
            walkthroughVideos: [
                {
                    name: "Creating your first learning experience from scratch",
                    description: "Learn how to get started with building your first learning experience from scratch.",
                    thumbnailPath: "/static/general/img/thumbnails/create.png",
                    href: "https://vimeo.com/891490281/16a981813f?share=copy",
                },
                {
                    name: "Sharing learning experiences as links",
                    description: "Learn how to share a learning experience with reviewers or learners using a link.",
                    thumbnailPath: "/static/general/img/thumbnails/share.png",
                    href: "https://vimeo.com/891592837/2213ec89ac?share=copy",
                },
                {
                    name: "Creating your first landing page",
                    description: "Learn how to create landing pages containing multiple learning experiences.",
                    thumbnailPath: "/static/general/img/thumbnails/page.png",
                    href: "https://vimeo.com/892114856/8754d3d338?share=copy",
                },
                {
                    name: "Adding videos to your learning experiences",
                    description: "Learn how to incorporate videos into your learning experiences.",
                    thumbnailPath: "/static/general/img/thumbnails/addingvideos.png",
                    href: "https://vimeo.com/891493887/49be7fb5be?share=copy",
                },
                {
                    name: "Custom GPT feature walkthrough",
                    description: "Learn how to add chat-based interactions to your learning experiences.",
                    thumbnailPath: "/static/general/img/thumbnails/gpt.png",
                    href: "https://vimeo.com/1025552759/11908636a0",
                },
            ],
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        role() {
            return this.$store.getters.userRole
        },
        memberships() {
            return this.$store.getters.memberships
        },
        membershipCount() {
            return this.memberships.length
        },
        freeTierMembership() {
            return this.$store.getters.freeTierMembership
        },
        hasFreeTierMembership() {
            return Boolean(this.freeTierMembership)
        },
        currentPricingTier() {
            return this.$store.getters.currentPricingTier
        },
        currentPricingTierName() {
            return this.$store.getters.currentPricingTierName
        },
        currentPricingTierReadout() {
            return `You're currently on the ${this.currentPricingTierName} plan. Click to upgrade your workspace.`
        },        
        showCommunityCreateBtn() {
            return this.membershipCount === 0 || this.legacyCommunityOrgMembership
        },
        legacyCommunityOrgMembership() {
            return this.memberships.find((m) => m.organization.is_legacy_community)
        },
        communityUserOnly() {
            return !this.userIsAnonymous && this.membershipCount == 0
        },
        newOrganizationIsValid() {
            return Boolean(this.newOrganization.name)
        },
        user() {
            return this.$store.getters.user
        },
        userHasAcceptedCommunityTerms() {
            return this.$store.getters.hasAcceptedCommunityTerms
        },
        activeMembership() {
            return this.$store.getters.activeMembership
        },
        isAuthorOrAdmin() {
            return this.role === "author" || this.role === "admin"
        },
        isStudent() {
            return this.role === "student"
        },
        isAdmin() {
            return this.role === "admin"
        },
        isStaff() {
            return this.user.is_staff
        },
        currentOrganization() {
            return this.$store.getters.currentOrganization
        },
        // The URL for the current organization's custom logo, if present.
        currentOrganizationCustomLogoUrl() {
            // Don't display the organization's logo if the custom branding feature is
            // not enabled.
            if (!this.currentOrganization.ff_custom_branding) {
                return null
            }
            if (this.currentOrganization.logo_image) {
                return this.currentOrganization.logo_image.signed_url
            }
            return null
        },
        // If true, the current organization's custom logo is shown in the workspace
        // dropdown.
        showOrganizationCustomLogo() {
            return this.currentOrganizationCustomLogoUrl && !this.communityViewActive
        },
        communityViewActive() {
            return this.$route.path.startsWith("/community")
        },
        pageViewActive() {
            return ["PageViewer", "PageViewerShare"].includes(this.$route.name)
        },
        organizationBillingEnabled() {
            return this.$store.getters.organizationBillingEnabled
        },
        // If true, navbar remains fixed to top of view.
        navbarFixed() {
            // Fix navbar to top whenever visible, except for in page share view.
            return !this.hideNav && this.$route.name !== "PageViewerShare"
        },
        hideNav() {
            return this.$route.meta.caseSessionIsActive || this.$route.meta.hideTopNav
        },
        allowCustomLogo() {
            return this.$route.meta.allowCustomLogo
        },
        customLogoUrl() {
            return this.$store.getters.customLogoUrl
        },
        customLogoNotConfigured() {
            return this.customLogoUrl === null
        },
        hideLoginPrompts() {
            return this.$route.meta.hideLoginPrompts
        },
        orgDropdownLabel() {
            if (this.currentOrganization.id && !this.communityViewActive) {
                return this.currentOrganization.name
            }
            return "Community"
        },
        // returns true if the user is anonymous.
        userIsAnonymous() {
            return this.$store.getters.userIsAnonymous
        },
        // used to highlight the profile dropdown toggle when the case history view is active
        profileView() {
            return this.$route.path == "/history/"
        },
        supportFormFields() {
            const fields = [
                {
                    name: "",
                    placeholder: "Describe the issue you're experiencing here...",
                    key: "message",
                    type: "textarea",
                    rows: 10,
                    required: true,
                },
            ]
            // add the email field if the user is anonymous
            if (this.userIsAnonymous) {
                fields.unshift({
                    name: "Email",
                    key: "email",
                    type: "email",
                    required: true,
                })
            }
            return fields
        },
        isViewingCommunityPublishers() {
            return this.$route.name == "CommunityPublishersAdmin"
        },
        isViewingOrgManage() {
            return ["OrganizationDetails", "LMSIntegrationSettings"].includes(
                this.$route.name
            )
        },
        creativeCommonsLicenseUrl() {
            return this.$store.getters.creativeCommonsLicenseUrl
        },
        selectedTourSteps() {
            return this.tourSteps[this.selectedTourId]
        },
    },
    methods: {
        logout() {
            http.post('/logout/').then(() => {
                window.location.href = '/login/'
            })
        },
        // set the user's active organization
        setActiveMembership(membership) {
            this.$store.dispatch("addLoad")
            let vm = this
            http.post("/api/set-active-org/", {
                membershipID: membership.id,
            }).then(function () {
                if (membership.role == "student") {
                    vm.$router.push({ name: "CaseLibrary" })
                } else if (vm.$route.name == "LearnerDetail") {
                    // Navigate away from member detail when changing orgs.
                    vm.$router.push({ name: "MemberList" })
                } else if (vm.$route.name == "PageEditor") {
                    // Navigate away from page editor when changing orgs.
                    vm.$router.push({ name: "Pages" })
                } else if (vm.$route.name === "OrganizationLibrary" && vm.$route.query.folder) {
                    // Navigate out of any folder being viewed.
                    vm.$router.push({ name: "OrganizationLibrary" })
                }

                if (vm.communityViewActive || vm.$route.name === "LearnerDefault") {
                    // Community or Learner view is active, so go to root.
                    window.location = "/"
                    return
                } else if (vm.$route.meta.parent == "CaseDetail") {
                    // Case detail page active, so go to case organization list.
                    vm.$router.push({ name: "OrganizationLibrary" })
                }

                // Refresh the page to get the new organization's data.
                location.reload()
            })
        },
        // save the user's feedback
        sendFeedback() {
            if (this.feedback === "") {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post("/api/feedback/", {
                message: this.feedback,
                view: this.$route.path,
                case: this.$store.getters.sessionCaseString,
                node: this.$store.getters.sessionNodeString,
            })
                .then(function () {
                    $("#feedback-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Thanks! Your feedback has been sent.",
                        data: {},
                    })
                    vm.feedback = ""
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // save the user's support request
        sendSupport() {
            // check form validity
            if (!this.$refs.supportForm.validate()) {
                return
            }
            let sessionCase = this.$store.getters.sessionCase
            let caseName = sessionCase ? `ID: ${sessionCase.id} - ${sessionCase.public_title}` : null
            this.modalLoading = true
            let vm = this
            http.post("/api/support/", {
                email: this.support.email,
                message: this.support.message,
                phone: this.support.phone,
                view: this.$route.path,
                caseName: caseName,
            })
                .then(function () {
                    $("#support-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Your support request has been submitted. Someone on our team will get back to you soon.",
                        data: {},
                    })
                    // reset support message
                    vm.support.message = ""
                })
                .finally(function () {
                    vm.modalLoading = false
                    vm.$refs.supportForm.resetValidate()
                })
        },
        collapseTopNav() {
            $(".navbar-collapse").collapse("hide")
        },
        openCommunityCreateModal() {
            if (this.legacyCommunityOrgMembership) {
                // If the user has a legacy community organization membership, show them a modal
                // prompting them to navigate to their personal workspace.
                $("#legacy-create-modal").modal("show")
            } else {
                // Prompt the user to switch to a workspae
                $("#create-first-organization-modal").modal("show")
            }
        },
        openCreateOrganization() {
            $("#create-organization-modal").modal("show")
        },
        createOrganization() {
            if (!this.$refs.createOrganizationForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post("/api/organizations/", this.newOrganization).then(function (response) {
                http.post("/api/set-active-org/", {
                    membershipID: response.data.id,
                }).then(async function () {
                    await vm.$store.dispatch("fetchAuth")

                    if (vm.$route.name == "OrganizationLibrary") {
                        vm.$router.push({
                            name: "OrganizationLibrary",
                            query: {
                                showTour: true,
                            },
                        })
                        vm.$router.go()
                    } else {
                        // Navigate to the new organization.
                        vm.$router.push({ name: "OrganizationLibrary" })
                    }

                    // Hide the modal the stop the loading spinner.
                    $("#create-organization-modal").modal("hide")
                    $("#create-first-organization-modal").modal("hide")
                    vm.modalLoading = false
                    // Start the 'tour' to tell the user how to switch between organizations.
                    vm.openWelcomeTourModal()
                })
            })
        },
        navigateToLegacyCommunityOrg() {
            this.modalLoading = true
            this.setActiveMembership(this.legacyCommunityOrgMembership)
        },
        acceptTerms() {
            this.modalLoading = true
            let vm = this
            let statementText = document.getElementById("community-statement").innerHTML
            http.post("/api/legal/accept-terms/", {
                userAccepts: this.termsCheckbox,
                statementText: statementText,
            })
                .then(function () {
                    // Fetch the user to update their terms acceptance.
                    vm.$store.dispatch("fetchAuth").then(function () {
                        vm.$router.push("/community/cases")
                        $("#community-modal").modal("hide")
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        displayRoleName(roleCode) {
            let roleName = roleNames[roleCode]
            if (!roleName) {
                // Fallback to the role code.
                return roleCode
            }
            return roleName
        },
        openWelcomeTourModal() {
            $("#welcome-modal").modal("show")
        },
        openPrivacyNoticeModal() {
            this.$refs.privacyNoticeUpdate.show()
        },
        startTour(tourId) {
            this.selectedTourId = tourId
            this.$tours["orgDropdownTour"].start()
        },
        // Handle an item being selected in the library searchbar.
        handleLibraryItemSelection(item) {
            let route
            // Construct a route depending on the item type that was selected.
            if (item.item_type === "owned_case") {
                route = {
                    name: "CaseSettings",
                    params: {
                        id: item.case.id,
                    },
                }
            } else if (item.item_type === "folder") {
                route = {
                    name: "OrganizationLibrary",
                    query: {
                        folder: item.id,
                    },
                }
            } else if (item.item_type === "community_reference") {
                route = {
                    name: "CommunityCaseDetail",
                    params: {
                        id: item.case.id,
                    },
                }
                // For community references, open the community detail view in another tab.
                window.open(this.$router.resolve(route).href, "_blank")
                return
            }
            this.$router.push(route).catch((error) => {
                if (error.name !== "NavigationDuplicated") {
                    console.error(error)
                }
            })
            this.collapseTopNav()
        },
        navigateToFreeTierMembership() {
            if (!this.freeTierMembership) {
                return
            }
            this.$router.push({ name: "OrganizationDetails" })
            this.setActiveMembership(this.freeTierMembership)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

// When topnav is fixed, need to provide a padding at the top of the screen to
// prevent overlap of main view content.
.topnav-container {
    padding-bottom: $navbar-spacer;
}

.top-nav {
    z-index: 200;
}

.toolbar-icon {
    font-size: 1.25rem;
    vertical-align: text-bottom;
}

.chevron {
    font-size: 0.75rem;
    color: $gray-600;
}

.navbar-toggler {
    font-size: 1rem;
}

.navbar-brand {
    margin-right: 0.8rem;
}

.nav-link {
    padding: 0.3rem 0.4rem !important;
    margin-left: 0.22rem;
    margin-right: 0.22rem;
    font-size: 15px;
    font-weight: 500;
    border-radius: $border-radius-sm;
    &.active {
        background-color: $nav-bg-active;
    }
}

.org-dropdown-list {
    min-width: 12rem;
    max-height: 70vh;
    overflow-y: scroll;
    a {
        font-size: 14px;
    }
}

.org-dropdown-label {
    max-width: 180px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.nav-link-org-dropdown {
    background-color: $search-bg;
    &:hover {
        background-color: darken(#f1f3f6, 3%);
    }
    transition: $transition-base;
}

.new-window-icon {
    vertical-align: -2px;
}

.workspace-screenshot {
    max-width: 400px;
}

.custom-logo-circle {
    margin-top: -7px;
    margin-bottom: -7px;
}

.dropdown-org-logo {
    height: 23px;
    width: 23px;
    margin-right: 6px;
    cursor: inherit;
}
</style>

<style lang="scss">
.v-step {
    max-width: 380px !important;
}
</style>